import { useCallback, useEffect, useState } from 'react';
import { usePopper } from 'react-popper';
import { Placement } from '@popperjs/core';
import { OffsetsFunction } from '@popperjs/core/lib/modifiers/offset';

import { spacing8 } from 'approot/shared/styles/spacing.style';

export type PopperOffset =
  | OffsetsFunction
  | [number | null | undefined, number | null | undefined]; // popper doesn't export this type

export function useContextualSurface(
  isActive: boolean,
  setIsActive: (state: boolean) => void,
  options?: {
    placement?: Placement;
    fallbackPlacements?: Placement[];
    offset?: PopperOffset;
    strategy?: 'fixed' | 'absolute';
  },
  useCapture = true
) {
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null
  );
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: options?.placement,
    strategy: options?.strategy,
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: options?.fallbackPlacements || [
            'top-start',
            'bottom-start',
          ],
        },
      },
      {
        name: 'offset',
        options: {
          offset: options?.offset || [0, spacing8],
        },
      },
    ],
  });

  const handleClose = useCallback(
    (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target !== referenceElement &&
        !referenceElement?.contains(target) &&
        !popperElement?.contains(target) &&
        isActive
      ) {
        setIsActive(false);
      }
    },
    [isActive, popperElement, referenceElement, setIsActive]
  );

  useEffect(() => {
    document.addEventListener('click', handleClose, useCapture);
    return () => {
      document.removeEventListener('click', handleClose, useCapture);
    };
  }, [handleClose, useCapture]);

  return {
    styles,
    attributes,
    referenceElement,
    setReferenceElement,
    popperElement,
    setPopperElement,
  };
}
